import { Box, FormControlLabel, Radio, RadioGroup, Stack } from '@mui/material'
import { InfoIcon } from '@phosphor-icons/react/dist/ssr'
import { InfoTooltip, tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import {
    NotificationSettingOptionsGroup,
    NotificationSettings,
    NotificationSettingsTableCopy,
} from 'ComponentLibrary/src/widgets/NotificationSettingsTable/types'
import { Control, Controller, UseFormRegister } from 'react-hook-form'

const ACTION_CELL_WIDTH = '196px'
const COMPACT_ACTION_CELL_WIDTH = '96px'
const COMPACT_ACTION_COLUMNS_WIDTH = '312px'
const DESCRIPTION_WIDTH = '50%'
const HEADER_ROW_HEIGHT = '32px'

export type NotificationSettingsTableProps = {
    settings: NotificationSettings
    updateSettings: (settings: NotificationSettings) => void
    rowGroups: NotificationSettingOptionsGroup[]
    additionalInfoText?: string
    control: Control<NotificationSettings>
    register: UseFormRegister<NotificationSettings>
    copy: NotificationSettingsTableCopy
    density?: 'regular' | 'compact'
}

export const NotificationSettingsTable = ({ settings, rowGroups, control, copy, density = 'regular' }: NotificationSettingsTableProps) => {
    const isCompact = density === 'compact'
    const actionCellWidth = isCompact ? COMPACT_ACTION_CELL_WIDTH : ACTION_CELL_WIDTH
    const actionColumnsWidth = isCompact ? COMPACT_ACTION_COLUMNS_WIDTH : `calc(100% - ${DESCRIPTION_WIDTH})`
    const actionColumnsGap = isCompact ? tokens.SizingSpacing150 : tokens.SizingSpacingNone
    const descriptionWidth = isCompact ? `calc(100% - ${COMPACT_ACTION_COLUMNS_WIDTH})` : DESCRIPTION_WIDTH

    return (
        <Stack direction="column" position="relative">
            <Stack
                top={0}
                position="sticky"
                direction="row"
                alignItems="center"
                justifyContent="end"
                bgcolor={tokens.ColorSurfaceDefault}
                zIndex={1}
                gap={tokens.SizingSpacing400}
                minHeight={HEADER_ROW_HEIGHT}
            >
                <Stack
                    direction="row"
                    width={actionColumnsWidth}
                    justifySelf="end"
                    justifyItems="end"
                    justifyContent="space-between"
                    alignItems="center"
                    textAlign="center"
                    gap={actionColumnsGap}
                >
                    <Stack direction="row" alignItems="center" justifyContent="center" width={actionCellWidth}>
                        <Typography variant="headline5" color={tokens.ColorTextDefault}>
                            {copy.off}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" justifyContent="center" width={actionCellWidth}>
                        <Typography variant="headline5" color={tokens.ColorTextDefault}>
                            {copy.immediate}
                        </Typography>
                    </Stack>
                    <Stack
                        direction="row"
                        gap={tokens.SizingSpacing025}
                        alignItems="center"
                        justifyContent="center"
                        width={actionCellWidth}
                    >
                        <Typography variant="headline5" color={tokens.ColorTextDefault}>
                            {copy.batch}
                        </Typography>
                        <InfoTooltip
                            size={tokens.SizingIconS}
                            style={{ color: tokens.ColorIconSubtle }}
                            weight="bold"
                            text={copy.batchTooltip}
                        />
                    </Stack>
                </Stack>
            </Stack>
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                {rowGroups.map((group, groupIndex) => (
                    <Stack direction="column" key={groupIndex}>
                        <Stack
                            direction="column"
                            width={descriptionWidth}
                            height="32px"
                            justifyContent="center"
                            paddingX={tokens.SizingSpacing100}
                        >
                            <Stack direction={'row'} alignItems={'center'} spacing={tokens.SizingSpacing100}>
                                <group.titleIcon color={tokens.ColorIconBoldest} size={tokens.SizingIconS} weight="bold" />
                                <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                                    {group.groupTitle}
                                </Typography>
                            </Stack>
                        </Stack>
                        {group.additionalInfoText && (
                            <Stack
                                padding={tokens.SizingSpacing100}
                                direction={'row'}
                                alignItems={'center'}
                                gap={tokens.SizingSpacing100}
                                maxWidth="800px"
                            >
                                <InfoIcon
                                    size={tokens.SizingIconS}
                                    weight="bold"
                                    color={tokens.ColorIconSubtle}
                                    style={{ flexShrink: 0 }}
                                />
                                <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                                    {group.additionalInfoText}
                                </Typography>
                            </Stack>
                        )}
                        {group.notificationSettingsOptions.map((row, index) => (
                            <Stack
                                direction="row"
                                key={index}
                                alignItems="center"
                                minHeight="32px"
                                paddingX={tokens.SizingSpacing050}
                                borderBottom={`${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`}
                            >
                                <Stack
                                    alignItems="center"
                                    direction="row"
                                    width={descriptionWidth}
                                    paddingX={tokens.SizingSpacing050}
                                    spacing={tokens.SizingSpacing100}
                                >
                                    <Typography variant="textS" color={tokens.ColorTextBold}>
                                        {row.title}
                                    </Typography>
                                    {row.description && <InfoTooltip text={row.description} style={{ color: tokens.ColorIconSubtle }} />}
                                </Stack>

                                <Controller
                                    name={row.identifier}
                                    control={control}
                                    defaultValue={settings[row.identifier]}
                                    render={({ field }) => (
                                        <RadioGroup
                                            row
                                            sx={{ width: actionColumnsWidth }}
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        >
                                            <Stack direction="row" width="100%" justifyContent="space-between" gap={actionColumnsGap}>
                                                <Box width={actionCellWidth} display="flex" justifyContent="center">
                                                    {row.options.includes('do_not_send') && (
                                                        <FormControlLabel
                                                            sx={{ margin: 0 }}
                                                            value="do_not_send"
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: tokens.ColorIconDisabled,
                                                                        '&.Mui-checked': {
                                                                            color: row.disabled
                                                                                ? tokens.ColorIconDisabled
                                                                                : tokens.ColorIconPositive,
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: '16px',
                                                                            width: '16px',
                                                                            height: '16px',
                                                                        },
                                                                        padding: '6px',
                                                                    }}
                                                                />
                                                            }
                                                            label=""
                                                            disabled={row.disabled}
                                                        />
                                                    )}
                                                </Box>

                                                <Box width={actionCellWidth} display="flex" justifyContent="center">
                                                    {row.options.includes('immediate') && (
                                                        <FormControlLabel
                                                            sx={{ margin: 0 }}
                                                            value="immediate"
                                                            control={
                                                                <Radio
                                                                    disabled={row.disabled}
                                                                    sx={{
                                                                        color: tokens.ColorIconDisabled,
                                                                        '&.Mui-checked': {
                                                                            color: row.disabled
                                                                                ? tokens.ColorIconDisabled
                                                                                : tokens.ColorIconPositive,
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: '16px',
                                                                            width: '16px',
                                                                            height: '16px',
                                                                        },
                                                                        padding: '6px',
                                                                    }}
                                                                />
                                                            }
                                                            label=""
                                                            disabled={row.disabled}
                                                        />
                                                    )}
                                                </Box>

                                                <Box width={actionCellWidth} display="flex" justifyContent="center">
                                                    {row.options.includes('batch') && (
                                                        <FormControlLabel
                                                            sx={{ margin: 0 }}
                                                            value="batch"
                                                            control={
                                                                <Radio
                                                                    disabled={row.disabled}
                                                                    sx={{
                                                                        color: tokens.ColorIconDisabled,
                                                                        '&.Mui-checked': {
                                                                            color: row.disabled
                                                                                ? tokens.ColorIconDisabled
                                                                                : tokens.ColorIconPositive,
                                                                        },
                                                                        '& .MuiSvgIcon-root': {
                                                                            fontSize: '16px',
                                                                            width: '16px',
                                                                            height: '16px',
                                                                        },
                                                                        padding: '6px',
                                                                    }}
                                                                />
                                                            }
                                                            label=""
                                                            disabled={row.disabled}
                                                        />
                                                    )}
                                                </Box>
                                            </Stack>
                                        </RadioGroup>
                                    )}
                                />
                            </Stack>
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    )
}
